import { forwardRef, useState, useCallback, useRef } from 'react';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { IoNotificationsOutline } from 'react-icons/io5';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import LocationPickerModal from './LocationPickerModal';

type BulkApplyModalProps = {
  productId: number;
  variantCount: number;
  itemName: string;
  unitLabel: string;
  onSaved: () => void;
};

type BulkFormData = {
  storage_location: string;
  stock: string;
  low_stock_alert: string;
  cost_price: string;
  selling_price: string;
  barcode_action: 'GENERATE_MISSING' | 'OVERWRITE_ALL' | '';
};

const INITIAL_FORM: BulkFormData = {
  storage_location: '',
  stock: '',
  low_stock_alert: '',
  cost_price: '',
  selling_price: '',
  barcode_action: '',
};

const BulkApplyModal = forwardRef<ModalMethods, BulkApplyModalProps>(
  ({ productId, variantCount, itemName, unitLabel, onSaved }, ref) => {
    const [formData, setFormData] = useState<BulkFormData>({ ...INITIAL_FORM });
    const [isSaving, setIsSaving] = useState(false);
    const [storageBinId, setStorageBinId] = useState<number | null>(null);
    const locationPickerRef = useRef<ModalMethods>(null);

    const handleClose = useCallback(() => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    }, [ref]);

    const handleFieldChange = useCallback((field: keyof BulkFormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleClearLocation = useCallback(() => {
      setFormData((prev) => ({ ...prev, storage_location: '' }));
      setStorageBinId(null);
    }, []);

    const handleBarcodeAction = useCallback((action: 'GENERATE_MISSING' | 'OVERWRITE_ALL') => {
      setFormData((prev) => ({
        ...prev,
        barcode_action: prev.barcode_action === action ? '' : action,
      }));
    }, []);

    const handleSave = useCallback(async () => {
      const payload: Record<string, unknown> = { apply_to_all: true };
      let hasField = false;

      if (formData.selling_price !== '') {
        payload.selling_price = parseFloat(formData.selling_price);
        hasField = true;
      }

      if (formData.cost_price !== '') {
        payload.cost_price = parseFloat(formData.cost_price);
        hasField = true;
      }

      if (formData.stock !== '') {
        payload.stock = parseFloat(formData.stock);
        hasField = true;
      }

      if (formData.low_stock_alert !== '') {
        payload.low_stock_alert = parseFloat(formData.low_stock_alert);
        hasField = true;
      }

      if (formData.storage_location.trim()) {
        payload.storage_location = formData.storage_location.trim();

        if (storageBinId) {
          payload.storage_bin_id = storageBinId;
        }

        hasField = true;
      }

      if (formData.barcode_action) {
        payload.barcode_action = formData.barcode_action;
        hasField = true;
      }

      if (!hasField) {
        toasts('error', 'Please fill at least one field', 'bulk-apply-empty');

        return;
      }

      try {
        setIsSaving(true);
        const response = await api.postRequest(
          `readymade/${productId}/variants/bulk-action`,
          payload
        );

        if (response.status) {
          toasts('success', 'Bulk apply successful', 'bulk-apply-success');
          setFormData({ ...INITIAL_FORM });
          setStorageBinId(null);
          handleClose();
          onSaved();
        } else {
          toasts('error', response.message || 'Bulk apply failed', 'bulk-apply-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'bulk-apply-error');
        }
      } finally {
        setIsSaving(false);
      }
    }, [formData, storageBinId, productId, handleClose, onSaved]);

    const headerComponent = (
      <div className="flex items-center justify-between w-full">
        <button
          type="button"
          onClick={handleClose}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <HiOutlineArrowLeft size={20} />
          <span className="font-inter font-medium text-sm">Back</span>
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="px-2 py-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex gap-8">
              <div>
                <p className="text-xs text-gray-500 font-inter mb-1">Variant Name</p>
                <p className="font-inter font-bold text-xl text-gray-900">
                  {variantCount} Variants
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-inter mb-1">Item Name</p>
                <p className="font-inter font-bold text-xl text-gray-900">{itemName || '—'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleBarcodeAction('GENERATE_MISSING')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
                  formData.barcode_action === 'GENERATE_MISSING'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Only Missing
              </button>
              <button
                type="button"
                onClick={() => handleBarcodeAction('OVERWRITE_ALL')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors border ${
                  formData.barcode_action === 'OVERWRITE_ALL'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Overwrite all
              </button>
            </div>
          </div>

          <hr className="border-gray-200 mb-6" />

          <div className="space-y-5">
            <div>
              <p className="text-sm text-gray-700 font-inter font-medium mb-1.5">Location</p>
              <div
                className="border border-gray-200 rounded-xl px-4 py-3 bg-white cursor-pointer hover:border-gray-400 transition-colors"
                onClick={() => locationPickerRef.current?.show()}
              >
                <p
                  className={`text-sm font-inter ${
                    formData.storage_location ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  {formData.storage_location || 'Warehouse • Rack A • Bin 14'}
                </p>
              </div>
              {formData.storage_location && (
                <button
                  type="button"
                  onClick={handleClearLocation}
                  className="text-xs text-gray-500 hover:text-gray-700 mt-1 float-right font-inter"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-700 font-inter font-medium mb-1.5">
                  Quantity Level
                </p>
                <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => handleFieldChange('stock', e.target.value)}
                    className="w-full outline-none text-sm font-inter text-gray-800"
                    placeholder={`0 (${unitLabel})`}
                  />
                  <IoNotificationsOutline size={18} className="text-gray-400 flex-shrink-0" />
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-700 font-inter font-medium mb-1.5">Low Stock</p>
                <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white flex items-center gap-2">
                  <input
                    type="number"
                    value={formData.low_stock_alert}
                    onChange={(e) => handleFieldChange('low_stock_alert', e.target.value)}
                    className="w-full outline-none text-sm font-inter text-gray-800"
                    placeholder={`0 (${unitLabel})`}
                  />
                  <IoNotificationsOutline size={18} className="text-gray-400 flex-shrink-0" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-700 font-inter font-medium mb-1.5">Cost Price</p>
                <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white flex items-center gap-2">
                  <span className="text-sm text-gray-500 font-inter">₹</span>
                  <input
                    type="number"
                    value={formData.cost_price}
                    onChange={(e) => handleFieldChange('cost_price', e.target.value)}
                    className="w-full outline-none text-sm font-inter text-gray-800"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-700 font-inter font-medium mb-1.5">Selling Price</p>
                <div className="border border-gray-200 rounded-xl px-4 py-3 bg-white flex items-center gap-2">
                  <span className="text-sm text-gray-500 font-inter">₹</span>
                  <input
                    type="number"
                    value={formData.selling_price}
                    onChange={(e) => handleFieldChange('selling_price', e.target.value)}
                    className="w-full outline-none text-sm font-inter text-gray-800"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <LocationPickerModal
          ref={locationPickerRef}
          subtitle="Bulk apply"
          onLocationSelect={(location, binId) => {
            handleFieldChange('storage_location', location);
            setStorageBinId(binId);
          }}
        />
      </Modal>
    );
  }
);

BulkApplyModal.displayName = 'BulkApplyModal';

export default BulkApplyModal;
